'use client'

import { Accordion, AccordionItem } from '@lastbrand/web-ui/accordion'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { Fragment } from 'react'

import { MiniPdpOptionsSummarySegment } from '../miniPDPProductOptions.helpers'

const ACCORDION_ITEM_VALUE = 'mini-pdp-options'

const segmentListCss = css({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 'space-12',
  // flex items default to min-width auto; allow the summary to shrink/wrap instead of pushing
  // the accordion chevron out of the trigger row
  minWidth: 0,
  textAlign: 'left',
})

const segmentCss = css({
  display: 'flex',
  alignItems: 'baseline',
  gap: 'space-4',
})

const segmentLabelCss = css({
  textStyle: 'title-12',
})

const segmentValueCss = css({
  textStyle: 'body-12',
  textTransform: 'none',
  letterSpacing: 0,
})

const segmentDividerCss = css({
  width: '1px',
  alignSelf: 'stretch',
  backgroundColor: 'black-trans-20',
})

const contentWrapperCss = css.raw({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space-24',
  // Let the accordion own row spacing.
  '& [data-testid="product-property"]': { marginBottom: 0 },
})

export interface MiniPDPCollapsibleOptionsDSProps {
  summarySegments: MiniPdpOptionsSummarySegment[]
  emptySummaryLabel: string
  children: React.ReactNode
  isExpanded: boolean
  onToggle: (nextExpanded: boolean) => void
}

export function MiniPDPCollapsibleOptionsDS({
  summarySegments,
  emptySummaryLabel,
  children,
  isExpanded,
  onToggle,
}: MiniPDPCollapsibleOptionsDSProps) {
  // '' (not undefined) is the controlled-closed sentinel: Radix treats value={undefined} as
  // switching to uncontrolled mode.
  return (
    <Accordion
      type="single"
      collapsible
      value={isExpanded ? ACCORDION_ITEM_VALUE : ''}
      onValueChange={next => {
        onToggle(next === ACCORDION_ITEM_VALUE)
      }}
    >
      <AccordionItem
        value={ACCORDION_ITEM_VALUE}
        contentWrapperCss={contentWrapperCss}
        title={
          <span className={segmentListCss}>
            {summarySegments.length > 0 ? (
              summarySegments.map((segment, index) => (
                <Fragment key={`${segment.label}-${index}`}>
                  <span className={segmentCss}>
                    <span className={segmentLabelCss}>{`${segment.label}:`}</span>
                    {segment.value && <span className={segmentValueCss}>{segment.value}</span>}
                  </span>
                  {index < summarySegments.length - 1 && <span className={segmentDividerCss} aria-hidden="true" />}
                </Fragment>
              ))
            ) : (
              <span className="sr-only">{emptySummaryLabel}</span>
            )}
          </span>
        }
      >
        {children}
      </AccordionItem>
    </Accordion>
  )
}
